/* eslint-disable react/forbid-prop-types */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, Button, alpha, Stack, Tooltip,
    Select, MenuItem,
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { buildResolvedDomainUrl } from 'src/utils/resolvedDomainUrl';
import { common, grey } from 'src/theme/palette';

/* ──────────────────────────────────────────────────────────────────────────
   SendFormsPreview (rev 5)
   ────────────────────────
   Always-rendered, sticky preview panel for the Send Forms wizard with:

   - Channel switcher at the top, one button per selected channel + an
     extra "PDF on submit" button showing what the rendered PDF will look
     like to the practice after the patient submits.
   - The active channel determines the preview frame.
   - Inline-editable email subject/body and WhatsApp body
     (`contenteditable`) — edits round-trip via `onTemplateChange`.
   - Visibly non-interactive "Start form" CTA in the email frame
     (striped gradient + "preview only" caption + cursor: not-allowed).
   - "preview" watermark in the top-right of every preview frame.
   - "Hidden from patient" callout listing any `practice_field_values`
     entries where `visible_to_patient=false`.
   ────────────────────────────────────────────────────────────────────────── */

const CHANNELS = {
    EMAIL: 'EMAIL',
    WHATSAPP: 'WHATSAPP',
    COPY_LINK: 'COPY_LINK',
    QR: 'QR',
    PDF: 'PDF',
};

const PREVIEW_ACCESS_TOKEN = 'a1b2c3d4-0000-4000-8000-000000000000';

// URL building delegates to the shared utility in
// src/utils/resolvedDomainUrl.js. The legacy fallback used to be
// "https://dental-sem.co.uk/..." regardless of host; the shared util
// uses window.location.origin instead, which keeps the preview
// truthful when the wizard is opened on staging or localhost. If the
// practice has a custom_subdomain configured, behaviour is unchanged.
const buildPreviewFormUrl = (consentConfig) =>
    buildResolvedDomainUrl({
        consentConfig,
        pathPrefix: 'consent',
        token: PREVIEW_ACCESS_TOKEN,
    });

const safeStr = (v) => (v == null ? '' : String(v));

const buildFormNameList = (forms) => {
    if (!Array.isArray(forms) || forms.length === 0) return 'Form';
    return forms
        .map((f) => f?.name || f?.title || f?.form_name || 'Form')
        .filter(Boolean)
        .join(', ');
};

// Merge-tag substitution matching the BE renderer.
const MERGE_TAG_RE = /\$\{([a-zA-Z_][a-zA-Z0-9_]*)\}|\$([a-zA-Z_][a-zA-Z0-9_]*)/g;

const resolveTags = (text, tags) => {
    if (!text) return '';
    return String(text).replace(MERGE_TAG_RE, (full, braceName, bareName) => {
        const key = braceName || bareName;
        if (!key) return full;
        if (Object.prototype.hasOwnProperty.call(tags, key)) {
            const value = tags[key];
            if (value === '' || value == null) return full;
            return String(value);
        }
        return full;
    });
};

const DEFAULT_EMAIL_SUBJECT = 'Your consent form from $practice_name';
const DEFAULT_EMAIL_BODY = `Hi $patient_first_name,

Please review and sign the attached form so we can prepare for your visit.

Thanks,
$practice_name`;
const DEFAULT_WHATSAPP_BODY = `Hi $patient_first_name, please complete this short form for us: $form_name. It only takes a moment — $form_url. Thanks, $practice_name.`;

// ── Channel count badge — rendered inline in dropdown options ─────────────
const ChannelCountBadge = ({ count }) => (
    <Box
        component="span"
        sx={{
            ml: 0.75,
            bgcolor: alpha('#919EAB', 0.2),
            color: '#637381',
            fontSize: '9px',
            fontWeight: 700,
            px: 0.5,
            borderRadius: '999px',
            minWidth: 14,
            textAlign: 'center',
            display: 'inline-block',
            lineHeight: 1.4,
        }}
    >
        {count}
    </Box>
);
ChannelCountBadge.propTypes = { count: PropTypes.number.isRequired };

// ── Preview watermark — top-right corner ──────────────────────────────────
const PreviewWatermark = () => (
    <Box
        sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: alpha('#919EAB', 0.16),
            color: '#637381',
            fontSize: '9px',
            letterSpacing: '0.1em',
            fontWeight: 700,
            textTransform: 'uppercase',
            px: 0.75,
            py: '2px',
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 2,
        }}
    >
        preview
    </Box>
);

// ── Inline-editable text (contenteditable) ────────────────────────────────
const InlineEditable = ({ value, onChange, multiline, ariaLabel, sxText }) => {
    const ref = useRef(null);

    // Keep DOM in sync when value changes from outside. We don't blindly
    // replace innerText every render — that would steal the user's caret.
    useEffect(() => {
        if (!ref.current) return;
        if (ref.current.innerText !== value) {
            ref.current.innerText = value || '';
        }
    }, [value]);

    const handleInput = () => {
        if (!ref.current) return;
        const next = ref.current.innerText;
        if (onChange) onChange(next);
    };

    return (
        <Box
            ref={ref}
            role="textbox"
            aria-label={ariaLabel}
            aria-multiline={multiline ? 'true' : 'false'}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            sx={{
                position: 'relative',
                borderRadius: '4px',
                px: 0.5,
                py: 0.25,
                mx: -0.5,
                my: -0.25,
                outline: 'none',
                cursor: 'text',
                border: '1px dashed transparent',
                whiteSpace: multiline ? 'pre-wrap' : 'normal',
                wordBreak: 'break-word',
                transition: 'border-color 120ms ease, background-color 120ms ease',
                '&:hover': {
                    borderColor: common.mintyZest,
                    backgroundColor: alpha(common.mintyZest, 0.04),
                },
                '&:hover::before': {
                    content: '"✎ click to edit"',
                    position: 'absolute',
                    top: -18,
                    left: 0,
                    background: common.mintyZest,
                    color: 'white',
                    fontSize: '9px',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                },
                '&:focus': {
                    borderColor: common.mintyZest,
                    backgroundColor: alpha(common.mintyZest, 0.04),
                },
                ...sxText,
            }}
        />
    );
};
InlineEditable.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    multiline: PropTypes.bool,
    ariaLabel: PropTypes.string,
    sxText: PropTypes.object,
};
InlineEditable.defaultProps = {
    value: '',
    onChange: null,
    multiline: false,
    ariaLabel: 'Editable text',
    sxText: null,
};

// ── Faux non-interactive "Start form" CTA ─────────────────────────────────
const FauxStartFormButton = () => (
    <Box
        component="span"
        aria-disabled="true"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 1.5,
            px: 1.75,
            py: '7px',
            background: `repeating-linear-gradient(45deg, ${common.mintyZest}, ${common.mintyZest} 6px, #007B40 6px, #007B40 12px)`,
            color: 'white',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
            opacity: 0.9,
            cursor: 'not-allowed',
            border: '1px dashed rgba(255,255,255,0.5)',
            userSelect: 'none',
        }}
    >
        Start form
        <Box
            component="span"
            sx={{
                fontSize: '9px',
                background: 'rgba(0,0,0,0.25)',
                px: 0.75,
                py: '1px',
                borderRadius: '3px',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
            }}
        >
            preview only
        </Box>
    </Box>
);

// ── Email preview frame ───────────────────────────────────────────────────
const EmailFrame = ({
    practiceName, senderEmail, subject, body, onSubjectChange, onBodyChange,
}) => (
    <Box
        sx={{
            position: 'relative',
            bgcolor: 'white',
            border: '1px solid',
            borderColor: alpha(grey[500], 0.18),
            borderRadius: '16px',
            p: 2,
            overflow: 'hidden',
        }}
    >
        <PreviewWatermark />
        <Stack direction="row" alignItems="center" spacing={1} sx={{ pb: 1, mb: 1.25, borderBottom: '1px solid', borderColor: alpha(grey[500], 0.16) }}>
            <Box
                sx={{
                    width: 28, height: 28, borderRadius: '50%',
                    bgcolor: alpha(common.mintyZest, 0.15),
                    color: common.mintyZest,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '11px',
                }}
            >
                {(practiceName || 'P').slice(0, 2).toUpperCase()}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1C252E' }} noWrap>
                    {practiceName || 'Your Practice'}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#637381' }} noWrap>
                    {senderEmail}
                </Typography>
            </Box>
        </Stack>

        <InlineEditable
            value={subject}
            onChange={onSubjectChange}
            ariaLabel="Email subject"
            sxText={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#1C252E',
                mb: 1,
            }}
        />
        <InlineEditable
            value={body}
            onChange={onBodyChange}
            multiline
            ariaLabel="Email body"
            sxText={{
                fontSize: '12.5px',
                color: '#454F5B',
                lineHeight: 1.55,
            }}
        />

        <FauxStartFormButton />
    </Box>
);
EmailFrame.propTypes = {
    practiceName: PropTypes.string,
    senderEmail: PropTypes.string,
    subject: PropTypes.string,
    body: PropTypes.string,
    onSubjectChange: PropTypes.func,
    onBodyChange: PropTypes.func,
};
EmailFrame.defaultProps = {
    practiceName: '',
    senderEmail: '',
    subject: '',
    body: '',
    onSubjectChange: null,
    onBodyChange: null,
};

// ── WhatsApp preview frame ────────────────────────────────────────────────
const WhatsAppFrame = ({ body, formLink, onBodyChange }) => (
    <Box
        sx={{
            position: 'relative',
            bgcolor: '#ECE5DD',
            backgroundImage: 'radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)',
            backgroundSize: '12px 12px',
            border: '1px solid',
            borderColor: alpha(grey[500], 0.18),
            borderRadius: '16px',
            p: 2,
            minHeight: 200,
        }}
    >
        <PreviewWatermark />
        <Box
            sx={{
                bgcolor: '#DCF8C6',
                borderRadius: '8px 8px 8px 2px',
                p: 1.25,
                pb: 2,
                maxWidth: '90%',
                fontSize: '12px',
                color: '#1C252E',
                position: 'relative',
                boxShadow: '0 1px 1px rgba(0,0,0,0.06)',
            }}
        >
            <InlineEditable
                value={body}
                onChange={onBodyChange}
                multiline
                ariaLabel="WhatsApp message"
                sxText={{
                    fontSize: '12px',
                    color: '#1C252E',
                    lineHeight: 1.5,
                }}
            />
            <Typography
                component="div"
                sx={{
                    color: '#1E88E5',
                    mt: 0.75,
                    fontSize: '11px',
                    wordBreak: 'break-all',
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                }}
            >
                {formLink}
            </Typography>
            <Typography
                sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 8,
                    fontSize: '9px',
                    color: '#637381',
                }}
            >
                {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ✓✓
            </Typography>
        </Box>
    </Box>
);
WhatsAppFrame.propTypes = {
    body: PropTypes.string,
    formLink: PropTypes.string,
    onBodyChange: PropTypes.func,
};
WhatsAppFrame.defaultProps = {
    body: '',
    formLink: '',
    onBodyChange: null,
};

// ── Copy-link preview frame ───────────────────────────────────────────────
// FE-8: The preview MUST NOT be copiable. The displayed link is a placeholder
// (the real ``access_token`` is only minted after Send), so letting analysts
// copy it out of the preview risks them sharing a dead URL. Two guards:
//   1. URL text is rendered with ``user-select: none`` so a manual highlight
//      + ⌘C can't lift the string.
//   2. The Copy button is disabled and wrapped in a Tooltip explaining the
//      real link will be available on the Submissions row after Send fires.
// We keep the visual structure (URL box + button) so the analyst still sees
// what the patient experience will look like.
const CopyLinkFrame = ({ formLink }) => (
    <Box
        sx={{
            position: 'relative',
            bgcolor: 'white',
            border: '1px solid',
            borderColor: alpha(grey[500], 0.18),
            borderRadius: '12px',
            p: 2,
        }}
    >
        <PreviewWatermark />
        <Typography sx={{ fontSize: '12px', color: '#454F5B', mb: 1 }}>
            Paste this into any tool, email signature, or text:
        </Typography>
        <Box
            sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                bgcolor: '#F4F6F8',
                p: 1.25,
                borderRadius: '8px',
                fontFamily: 'ui-monospace, "SF Mono", monospace',
                fontSize: '12px',
                color: '#454F5B',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    pointerEvents: 'none',
                }}
            >
                {formLink}
            </Box>
            <Tooltip
                title="Copy is available on the submission after you Send"
                placement="top"
                arrow
            >
                {/* span wrapper so MUI Tooltip still fires on a disabled button */}
                <span>
                    <Button
                        size="small"
                        variant="contained"
                        disabled
                        sx={{
                            bgcolor: common.mintyZest,
                            '&.Mui-disabled': {
                                bgcolor: alpha(common.mintyZest, 0.4),
                                color: 'white',
                            },
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '11px',
                            borderRadius: '6px',
                            px: 1.25,
                            py: 0.25,
                        }}
                    >
                        Copy
                    </Button>
                </span>
            </Tooltip>
        </Box>
    </Box>
);
CopyLinkFrame.propTypes = { formLink: PropTypes.string };
CopyLinkFrame.defaultProps = { formLink: '' };

// ── QR preview frame ──────────────────────────────────────────────────────
const QRFrame = ({ formLink, formName }) => {
    const handleDownload = () => {
        try {
            const canvas = document.getElementById('send-forms-preview-qr');
            if (!canvas) return;
            const url = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(formName || 'form').replace(/\s+/g, '-').toLowerCase()}-qr.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (e) {
            // best-effort
        }
    };
    const handlePrint = () => {
        try {
            const canvas = document.getElementById('send-forms-preview-qr');
            const dataUrl = canvas ? canvas.toDataURL('image/png') : '';
            const w = window.open('', '_blank', 'width=420,height=520');
            if (!w) return;
            w.document.write(`
                <html>
                  <head><title>Print QR</title></head>
                  <body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif;">
                    <div style="text-align:center;">
                      <img src="${dataUrl}" alt="QR" style="width:240px;height:240px;" />
                      <div style="margin-top:12px;font-size:14px;font-weight:600;">${formName || 'Patient form'}</div>
                    </div>
                  </body>
                </html>
            `);
            w.document.close();
            setTimeout(() => { try { w.print(); } catch (err) { /* noop */ } }, 250);
        } catch (e) {
            // best-effort
        }
    };
    return (
        <Box
            sx={{
                position: 'relative',
                bgcolor: 'white',
                border: '1px solid',
                borderColor: alpha(grey[500], 0.18),
                borderRadius: '12px',
                p: 2,
                textAlign: 'center',
            }}
        >
            <PreviewWatermark />
            <Box
                sx={{
                    p: 1.5,
                    borderRadius: '12px',
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: alpha(grey[500], 0.16),
                    display: 'inline-block',
                }}
            >
                <QRCodeCanvas
                    id="send-forms-preview-qr"
                    value={formLink}
                    size={140}
                    level="M"
                    includeMargin={false}
                    bgColor="#FFFFFF"
                    fgColor="#1C252E"
                />
            </Box>
            <Typography sx={{ fontSize: '11px', color: '#454F5B', mt: 1 }}>
                Scannable on a tablet at reception
            </Typography>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', mt: 1 }}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={handleDownload}
                    startIcon={<Iconify icon="eva:download-fill" width={12} />}
                    sx={{
                        borderRadius: '8px', textTransform: 'none',
                        fontSize: '11px', fontWeight: 600,
                        borderColor: alpha(grey[500], 0.3), color: '#454F5B',
                    }}
                >
                    Download
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={handlePrint}
                    startIcon={<Iconify icon="eva:printer-fill" width={12} />}
                    sx={{
                        borderRadius: '8px', textTransform: 'none',
                        fontSize: '11px', fontWeight: 600,
                        borderColor: alpha(grey[500], 0.3), color: '#454F5B',
                    }}
                >
                    Print
                </Button>
            </Stack>
        </Box>
    );
};
QRFrame.propTypes = {
    formLink: PropTypes.string,
    formName: PropTypes.string,
};
QRFrame.defaultProps = { formLink: '', formName: '' };

// ── PDF on submit preview frame ───────────────────────────────────────────
const PdfOnSubmitFrame = ({ formName, patientName, practiceFieldValues }) => {
    const entries = Object.entries(practiceFieldValues || {});
    return (
        <Box
            sx={{
                position: 'relative',
                bgcolor: 'white',
                border: '1px solid',
                borderColor: alpha(grey[500], 0.18),
                borderRadius: '12px',
                p: 2,
                fontSize: '11px',
                color: '#454F5B',
            }}
        >
            <PreviewWatermark />
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1C252E' }}>
                {formName || 'Patient form'}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#637381', mb: 1 }}>
                Submitted by: {patientName || 'Patient'} · {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </Typography>
            {/* Skeleton patient answer lines */}
            {[80, 100, 60, 100].map((w, i) => (
                <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    sx={{
                        height: 6,
                        bgcolor: alpha(grey[500], 0.16),
                        borderRadius: '2px',
                        my: 0.5,
                        width: `${w}%`,
                    }}
                />
            ))}
            <Typography
                sx={{
                    mt: 1.5, mb: 0.5,
                    fontSize: '10px', fontWeight: 700,
                    color: '#1C252E',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                }}
            >
                Practice details
            </Typography>
            <Box
                sx={{
                    bgcolor: '#FFFBEA',
                    border: '1px solid #FDE68A',
                    borderRadius: '6px',
                    p: 1,
                }}
            >
                {entries.length === 0 ? (
                    <Typography sx={{ fontSize: '11px', color: '#637381' }}>
                        Practice fields will appear here.
                    </Typography>
                ) : (
                    entries.map(([key, v]) => (
                        <Typography key={key} sx={{ fontSize: '11px', color: '#1C252E' }}>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                                {v?.label || (key === 'system:treated_by' ? 'Treated by' : 'Field')}:
                            </Box>{' '}
                            {v?.display || v?.value || '—'}
                        </Typography>
                    ))
                )}
            </Box>
        </Box>
    );
};
PdfOnSubmitFrame.propTypes = {
    formName: PropTypes.string,
    patientName: PropTypes.string,
    practiceFieldValues: PropTypes.object,
};
PdfOnSubmitFrame.defaultProps = {
    formName: '',
    patientName: '',
    practiceFieldValues: null,
};

// ── Public component ─────────────────────────────────────────────────────
const SendFormsPreview = ({
    audienceMode,
    selectedPatient,
    selectedPatients,
    selectedForms,
    channels,
    channel,
    templateState,
    emailTemplate,
    whatsappTemplate,
    expiryHours,
    practiceName,
    consentConfig,
    practiceFieldValues,
    onTemplateChange,
}) => {
    const { user } = useAuth() || {};

    // Normalise active channel set.
    const channelList = useMemo(() => {
        const raw = Array.isArray(channels) && channels.length > 0
            ? channels
            : [channel].filter(Boolean);
        return raw
            .map((c) => (c === 'COPY' ? CHANNELS.COPY_LINK : c))
            .filter((c) => Object.values(CHANNELS).includes(c));
    }, [channels, channel]);

    const [activeChannel, setActiveChannel] = useState(channelList[0] || CHANNELS.EMAIL);

    // If the selected channel set changes (e.g. user un-ticks Email),
    // fall back to the first available channel — otherwise the preview
    // would be stuck on a channel the user has just removed.
    useEffect(() => {
        if (activeChannel === CHANNELS.PDF) return;
        if (!channelList.includes(activeChannel)) {
            setActiveChannel(channelList[0] || CHANNELS.EMAIL);
        }
    }, [channelList, activeChannel]);

    const isAnonymous = audienceMode === 'ANONYMOUS';
    const previewFormUrl = useMemo(
        () => buildPreviewFormUrl(consentConfig),
        [consentConfig],
    );

    const primaryPatient = useMemo(() => {
        if (audienceMode === 'ONE_PATIENT') return selectedPatient || null;
        if (audienceMode === 'PATIENT_LIST') return (selectedPatients && selectedPatients[0]) || null;
        return null;
    }, [audienceMode, selectedPatient, selectedPatients]);

    const senderEmail = safeStr(user?.email).trim() || 'no-reply@dental-sem.co.uk';

    const formNameList = useMemo(() => buildFormNameList(selectedForms), [selectedForms]);

    const tags = useMemo(() => ({
        patient_first_name: isAnonymous ? '' : safeStr(primaryPatient?.first_name).trim(),
        patient_last_name: isAnonymous ? '' : safeStr(primaryPatient?.last_name).trim(),
        practice_name: safeStr(practiceName).trim() || 'Your Practice',
        form_name: formNameList,
        form_url: previewFormUrl,
        form_link: previewFormUrl,
    }), [isAnonymous, primaryPatient, practiceName, formNameList, previewFormUrl]);

    // Resolved (merge-tagged) subject + body for the active channel.
    const emailSubjectRaw = (templateState?.subject) || emailTemplate?.subject || DEFAULT_EMAIL_SUBJECT;
    const emailBodyRaw = (templateState?.body) || emailTemplate?.body || DEFAULT_EMAIL_BODY;
    const whatsappBodyRaw = (templateState?.whatsappBody) || whatsappTemplate?.body || whatsappTemplate?.content || DEFAULT_WHATSAPP_BODY;

    const emailSubject = useMemo(
        () => resolveTags(emailSubjectRaw, tags),
        [emailSubjectRaw, tags],
    );
    const emailBody = useMemo(
        () => resolveTags(emailBodyRaw, tags),
        [emailBodyRaw, tags],
    );
    const whatsappBody = useMemo(
        () => resolveTags(whatsappBodyRaw, tags),
        [whatsappBodyRaw, tags],
    );

    // Build channel switcher buttons — one per selected channel plus
    // an always-present "PDF on submit" entry.
    const switcherButtons = useMemo(() => {
        const buttons = [];
        if (channelList.includes(CHANNELS.EMAIL)) {
            buttons.push({ value: CHANNELS.EMAIL, label: 'Email', icon: 'solar:letter-bold-duotone', count: selectedForms?.length });
        }
        if (channelList.includes(CHANNELS.WHATSAPP)) {
            buttons.push({ value: CHANNELS.WHATSAPP, label: 'WhatsApp', icon: 'mdi:whatsapp' });
        }
        if (channelList.includes(CHANNELS.COPY_LINK)) {
            buttons.push({ value: CHANNELS.COPY_LINK, label: 'Link', icon: 'solar:link-round-bold-duotone' });
        }
        if (channelList.includes(CHANNELS.QR)) {
            buttons.push({ value: CHANNELS.QR, label: 'QR', icon: 'solar:qr-code-bold-duotone' });
        }
        buttons.push({ value: CHANNELS.PDF, label: 'PDF on submit', icon: 'solar:document-bold-duotone' });
        return buttons;
    }, [channelList, selectedForms]);

    const patientName = useMemo(() => {
        if (!primaryPatient) return '';
        return [primaryPatient.first_name, primaryPatient.last_name].filter(Boolean).join(' ');
    }, [primaryPatient]);

    // Hidden-from-patient callout — list any practice_field_values entry
    // where `visible_to_patient === false`.
    const hiddenEntries = useMemo(() => {
        if (!practiceFieldValues) return [];
        return Object.values(practiceFieldValues).filter((v) => v && v.visible_to_patient === false);
    }, [practiceFieldValues]);

    const handleEmailSubjectChange = (next) => {
        if (onTemplateChange) onTemplateChange({ subject: next });
    };
    const handleEmailBodyChange = (next) => {
        if (onTemplateChange) onTemplateChange({ body: next });
    };
    const handleWhatsAppBodyChange = (next) => {
        if (onTemplateChange) onTemplateChange({ whatsappBody: next });
    };

    return (
        <Box
            aria-label="Live preview"
            sx={{
                position: 'sticky',
                top: 16,
                alignSelf: 'flex-start',
                width: '100%',
                maxWidth: 480,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
            }}
        >
            {/* Preview label + status */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Iconify
                        icon="solar:smartphone-bold-duotone"
                        width={14}
                        style={{ color: common.mintyZest }}
                    />
                    <Typography
                        sx={{
                            fontSize: '10.5px',
                            fontWeight: 700,
                            color: common.mintyZest,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                        }}
                    >
                        Live preview
                    </Typography>
                </Stack>
                <Tooltip title="Updates as you type" placement="top">
                    <Box
                        sx={{
                            fontSize: '10px',
                            color: '#637381',
                            border: '1px solid',
                            borderColor: alpha('#919EAB', 0.2),
                            bgcolor: 'white',
                            px: 1,
                            py: '2px',
                            borderRadius: '999px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: 6, height: 6, borderRadius: '50%',
                                bgcolor: common.mintyZest,
                            }}
                        />
                        Updates as you type
                    </Box>
                </Tooltip>
            </Stack>

            {/* Channel switcher — FE-7 correction 5: tab strip replaced by
                a compact Select dropdown that scales to N channels and
                doesn't overflow at 380px. */}
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                    component="label"
                    htmlFor="send-forms-preview-channel-select"
                    sx={{
                        fontSize: '11px', fontWeight: 700, color: '#637381',
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}
                >
                    Preview
                </Typography>
                <Select
                    id="send-forms-preview-channel-select"
                    size="small"
                    value={activeChannel}
                    onChange={(e) => setActiveChannel(e.target.value)}
                    inputProps={{ 'aria-label': 'Preview channel' }}
                    sx={{
                        flex: 1,
                        bgcolor: 'white',
                        borderRadius: '8px',
                        fontSize: '12.5px',
                        '& .MuiSelect-select': {
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            py: 0.75,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha('#919EAB', 0.24),
                        },
                    }}
                >
                    {switcherButtons.map((b) => (
                        <MenuItem
                            key={b.value}
                            value={b.value}
                            sx={{
                                fontSize: '12.5px',
                                display: 'flex', alignItems: 'center', gap: 0.75,
                            }}
                        >
                            <Iconify icon={b.icon} width={14} />
                            <Box component="span">{b.label}</Box>
                            {b.count != null && <ChannelCountBadge count={b.count} />}
                        </MenuItem>
                    ))}
                </Select>
            </Stack>

            {/* Active preview frame */}
            {activeChannel === CHANNELS.EMAIL && (
                <EmailFrame
                    practiceName={tags.practice_name}
                    senderEmail={senderEmail}
                    subject={emailSubject}
                    body={emailBody}
                    onSubjectChange={handleEmailSubjectChange}
                    onBodyChange={handleEmailBodyChange}
                />
            )}
            {activeChannel === CHANNELS.WHATSAPP && (
                <WhatsAppFrame
                    body={whatsappBody}
                    formLink={tags.form_link}
                    onBodyChange={handleWhatsAppBodyChange}
                />
            )}
            {activeChannel === CHANNELS.COPY_LINK && (
                <CopyLinkFrame formLink={tags.form_link} />
            )}
            {activeChannel === CHANNELS.QR && (
                <QRFrame formLink={tags.form_link} formName={tags.form_name} />
            )}
            {activeChannel === CHANNELS.PDF && (
                <PdfOnSubmitFrame
                    formName={tags.form_name}
                    patientName={patientName}
                    practiceFieldValues={practiceFieldValues}
                />
            )}

            {/* Hidden-from-patient callout */}
            {hiddenEntries.length > 0 && (
                <Box
                    sx={{
                        mt: 0.5,
                        p: 1.25,
                        bgcolor: '#CAFDF5',
                        borderRadius: '8px',
                        fontSize: '11px',
                        color: '#155E75',
                        display: 'flex',
                        gap: 0.75,
                        alignItems: 'flex-start',
                    }}
                >
                    <Iconify icon="eva:lock-fill" width={14} style={{ marginTop: 1 }} />
                    <Box sx={{ flex: 1 }}>
                        {hiddenEntries.map((v, i) => (
                            <Box
                                // eslint-disable-next-line react/no-array-index-key
                                key={i}
                                sx={{ display: 'block', mb: i < hiddenEntries.length - 1 ? 0.5 : 0 }}
                            >
                                <Box component="strong">
                                    {v.label || 'Field'}: {v.display || v.value}
                                </Box>{' '}
                                will appear on the submitted PDF only — the patient won&apos;t see it.
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Footer summary */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '8px',
                    bgcolor: alpha(grey[500], 0.06),
                    border: '1px solid',
                    borderColor: alpha(grey[500], 0.12),
                    fontSize: '11px',
                    color: '#637381',
                }}
            >
                <Iconify icon="solar:clock-circle-bold-duotone" width={13} style={{ color: '#B76E00' }} />
                Link expires in{' '}
                <Box component="span" sx={{ fontWeight: 700, color: '#1C252E' }}>
                    {Math.max(1, Math.round(((expiryHours || 168) / 24)))} days
                </Box>
                <Box component="span" sx={{ mx: 0.5 }}>·</Box>
                <Box component="span" sx={{ fontWeight: 700, color: '#1C252E' }}>
                    {selectedForms?.length || 0}
                </Box>
                form{(selectedForms?.length || 0) === 1 ? '' : 's'}
                <Box component="span" sx={{ mx: 0.5 }}>·</Box>
                <Box component="span" sx={{ fontWeight: 700, color: '#1C252E' }}>
                    {channelList.length}
                </Box>
                channel{channelList.length === 1 ? '' : 's'}
            </Box>
        </Box>
    );
};

SendFormsPreview.propTypes = {
    audienceMode: PropTypes.oneOf(['ONE_PATIENT', 'PATIENT_LIST', 'ANONYMOUS']).isRequired,
    selectedPatient: PropTypes.object,
    selectedPatients: PropTypes.array,
    selectedForms: PropTypes.array.isRequired,
    channel: PropTypes.oneOf(['EMAIL', 'WHATSAPP', 'COPY', 'COPY_LINK', 'QR']),
    channels: PropTypes.arrayOf(PropTypes.string),
    templateState: PropTypes.shape({
        subject: PropTypes.string,
        body: PropTypes.string,
        whatsappBody: PropTypes.string,
    }),
    emailTemplate: PropTypes.object,
    whatsappTemplate: PropTypes.object,
    expiryHours: PropTypes.number,
    practiceName: PropTypes.string,
    consentConfig: PropTypes.shape({
        metadata: PropTypes.shape({
            url_style: PropTypes.string,
            custom_subdomain: PropTypes.string,
            base_domain: PropTypes.string,
        }),
    }),
    // Practice field values (system:treated_by + PracticeFormField pks).
    practiceFieldValues: PropTypes.object,
    onTemplateChange: PropTypes.func,
};

SendFormsPreview.defaultProps = {
    selectedPatient: null,
    selectedPatients: [],
    channel: 'EMAIL',
    channels: null,
    templateState: null,
    emailTemplate: null,
    whatsappTemplate: null,
    expiryHours: 168,
    practiceName: '',
    consentConfig: null,
    practiceFieldValues: null,
    onTemplateChange: null,
};

export default SendFormsPreview;

// Test helpers — exported as a named util so jest specs can verify the
// channel switcher behaviour without touching the public default export.
export { CHANNELS };
